import React, { useState, useEffect } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import Toster from "../../utils/Toaster";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLoader } from "../../contexts/LoaderContext";
import {
  handleAdminFetchContent,
  handleAdminUpdateContent,
} from "../../services/APIClient";
import { useDispatch } from "react-redux";
import { STATUS_CODES } from "../../utils/statusCode";
import { removeUserData } from "../../store/slices/userSlice";

const ContentPage = () => {
  const [selectedData, setSelectedData] = useState("");
  const { setLoading } = useLoader();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pageName = searchParams.get("pageName") || "aboutUs";

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const fetchContent = async () => {
      try {
        setLoading(true);
        const response = await handleAdminFetchContent({ pageName });

        if (response?.code === STATUS_CODES.SUCCESS) {
          setSelectedData(response.data.content || "");
        } else if (response?.code === STATUS_CODES.INVALID_TOKEN) {
          dispatch(removeUserData({}));
          navigate("/login");
        } else {
          Toster("Failed to fetch content", "error");
        }
      } catch (error) {
        Toster("An error occurred while fetching content", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [pageName, dispatch, navigate, setLoading]);

  const handleEditorChange = (value) => {
    // Do not strip any content here to preserve spacing
    setSelectedData(value);
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      // Only strip HTML to check plain text length
      const plainText = selectedData.replace(/<[^>]*>/g, "").trim();
      const contentLength = plainText.length;

      if (contentLength < 1) {
        Toster("Content is required", "error");
        return;
      }

      if (contentLength > 5000) {
        Toster("Content cannot exceed 5000 characters", "error");
        return;
      }

      const response = await handleAdminUpdateContent({
        name: pageName,
        content: selectedData, // Save original HTML content
      });

      if (response?.code === STATUS_CODES.SUCCESS) {
        Toster("Content updated successfully", "success");
      } else if (response?.code === STATUS_CODES.INVALID_TOKEN) {
        dispatch(removeUserData({}));
        navigate("/login");
      } else {
        Toster("Failed to update content", "error");
      }
    } catch (error) {
      Toster("An error occurred while saving content", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-4 sm:px-8 py-6 bg-gray-50 min-h-screen w-full">
      <div className="bg-white shadow-lg rounded-md p-6 w-full">
        {/* Page Title */}
        <h1 className="text-2xl font-semibold text-gray-800 mb-6 text-center capitalize">
          {pageName === "aboutUs"
            ? "About Us"
            : pageName === "termsCondition"
            ? "Terms & Conditions"
            : "Privacy Policy"}
        </h1>

        {/* React Quill Editor */}
        <div className="mb-6 w-full">
          <ReactQuill
            value={selectedData}
            onChange={handleEditorChange}
            modules={{
              toolbar: [
                [{ header: "1" }, { header: "2" }, { font: [] }],
                [{ list: "ordered" }, { list: "bullet" }],
                ["bold", "italic", "underline"],
                [{ align: [] }],
                ["link"],
              ],
            }}
            className="min-h-[300px]"
            placeholder="Please enter description"
          />
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            className="bg-gray-600 text-white py-3 px-6 rounded-md hover:bg-gray-700 transition"
          >
            Save Content
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContentPage;
